import { Box, Typography } from '@mui/material';
import { Button } from '@peritasai/ui-components';
import { useTranslation } from 'react-i18next';
import { DonutChart } from './DonutChart';

const panelSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '12px',
  width: '324px',
  // Fixed-width panel — must never be squeezed by its flex sibling
  // (the tray grid) even if that grid's content momentarily overflows.
  flexShrink: 0,
  padding: '16px',
  borderRadius: '8px',
  background: '#fff',
};

const sectionLabelSx = {
  color: '#94a3b8',
  fontSize: '10px',
  fontWeight: 600,
  lineHeight: '17.5px',
  letterSpacing: '1px',
  textTransform: 'uppercase' as const,
};

const cartIdBoxSx = {
  width: '292px',
  padding: '8px',
  borderRadius: '8px',
  backgroundColor: '#f3f3f3',
  textAlign: 'center' as const,
};

const cartIdValueSx = {
  color: '#000',
  fontSize: '24px',
  fontWeight: 600,
  letterSpacing: '-1.68px',
};

const donutRowSx = {
  display: 'flex',
  justifyContent: 'center',
};

const legendSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '11px',
};

const legendRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const legendLabelGroupSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const legendDotSx = (color: string) => ({
  width: '9px',
  height: '9px',
  borderRadius: '50%',
  backgroundColor: color,
  flexShrink: 0,
});

const legendTextSx = {
  color: '#141414',
  fontSize: '12px',
  fontWeight: 600,
};

const primaryActionSx = {
  width: '100%',
  height: '42px',
  borderRadius: '4px',
  backgroundColor: '#4652e2',
  color: '#fff',
  fontSize: '11.6px',
  fontWeight: 700,
  textTransform: 'none' as const,
};

type CartSummaryPanelProps = {
  cartReferenceNumber: string | null;
  onCartCount: number;
  pendingCount: number;
  isComplete: boolean;
  onPrimaryAction: () => void;
  disabled: boolean;
};

export const CartSummaryPanel = ({
  cartReferenceNumber,
  onCartCount,
  pendingCount,
  isComplete,
  onPrimaryAction,
  disabled,
}: CartSummaryPanelProps) => {
  const { t } = useTranslation(['periverse']);

  return (
    <Box sx={panelSx} data-testid="cart-summary-panel">
      <Typography sx={sectionLabelSx}>
        {t('pages.caseCartAssembly.progressScreen.cartIdLabel')}
      </Typography>
      <Box sx={cartIdBoxSx}>
        <Typography sx={cartIdValueSx}>
          {cartReferenceNumber ?? t('pages.caseCartAssembly.noCartAssigned')}
        </Typography>
      </Box>

      <Typography sx={sectionLabelSx}>
        {t('pages.caseCartAssembly.progressScreen.progressLabel')}
      </Typography>
      <Box sx={donutRowSx}>
        <DonutChart onCartCount={onCartCount} pendingCount={pendingCount} />
      </Box>

      <Box sx={legendSx}>
        <Box sx={legendRowSx}>
          <Box sx={legendLabelGroupSx}>
            <Box sx={legendDotSx('#901de4')} />
            <Typography sx={legendTextSx}>
              {t('pages.caseCartAssembly.progressScreen.onCartLegendLabel')}
            </Typography>
          </Box>
          <Typography sx={legendTextSx}>{onCartCount}</Typography>
        </Box>
        <Box sx={legendRowSx}>
          <Box sx={legendLabelGroupSx}>
            <Box sx={legendDotSx('#f2ff03')} />
            <Typography sx={legendTextSx}>
              {t('pages.caseCartAssembly.progressScreen.pendingLegendLabel')}
            </Typography>
          </Box>
          <Typography sx={legendTextSx}>{pendingCount}</Typography>
        </Box>
      </Box>

      <Button
        variant="contained"
        onClick={onPrimaryAction}
        disabled={disabled}
        sx={primaryActionSx}
        data-testid="cart-primary-action-button"
      >
        {isComplete
          ? t('pages.caseCartAssembly.progressScreen.closeLabel')
          : t('pages.caseCartAssembly.progressScreen.saveCartLabel')}
      </Button>
    </Box>
  );
};
